import { logger } from '../../../../shared/logger';

/**
 * 🔐 프론트엔드 JWT 유틸리티
 *
 * 🎯 핵심 철학:
 * - 브라우저 환경에서 안전한 JWT 토큰 관리
 * - localStorage 기반 토큰 저장
 * - API 요청 시 자동 헤더 추가
 */

// ============================================
// 🎯 JWT 토큰 관리
// ============================================

const JWT_TOKEN_KEY = 'inflomatrix_jwt_token';
const SESSION_ID_KEY = 'inflomatrix_session_id';
const REFRESH_TOKEN_KEY = 'inflomatrix_refresh_token';
const TENANT_ID_KEY = 'inflomatrix_tenant_id';

function looksLikeJwt(token: string): boolean {
  return token.split('.').length === 3;
}

export interface JWTTokenPayload {
  userId: string;
  entityId?: string;
  /** Login-linked internal entity — preferred when entityId is absent (AuthAPI JWT). */
  linkedEntityId?: string;
  entityType?: 'internal' | 'external' | 'user';
  tenantId: string;
  sessionId?: string;
  permissions?: string[];
  role?: string;
  /** external_entity contract tier — partner feature RBAC (D15) */
  partnerTierId?: string;
  stepUpAt?: string;
  mfaVerifiedAt?: string;
  exp?: number;
  iat?: number;
}

/**
 * JWT 토큰 저장
 */
export const setJWTToken = (token: string): void => {
  localStorage.setItem(JWT_TOKEN_KEY, token);
};

/**
 * JWT 토큰 조회
 */
export const getJWTToken = (): string | null => {
  return localStorage.getItem(JWT_TOKEN_KEY);
};

/**
 * JWT 토큰 제거
 */
export const removeJWTToken = (): void => {
  localStorage.removeItem(JWT_TOKEN_KEY);
};

/**
 * 세션 ID 저장 (refresh·세션 기반 인증용)
 */
export const setSessionId = (sessionId: string): void => {
  localStorage.setItem(SESSION_ID_KEY, sessionId);
};

/**
 * 세션 ID 조회 (레거시: accessToken 키에 sessionId만 저장된 경우 포함)
 */
export const getSessionId = (): string | null => {
  const stored = localStorage.getItem(SESSION_ID_KEY);
  if (stored) return stored;

  const legacyToken = getJWTToken();
  if (!legacyToken) return null;
  if (!looksLikeJwt(legacyToken)) return legacyToken;

  const payload = decodeJWTToken(legacyToken);
  return payload?.sessionId ?? null;
};

export const removeSessionId = (): void => {
  localStorage.removeItem(SESSION_ID_KEY);
};

export const setRefreshToken = (refreshToken: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const removeRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export interface AuthTokensInput {
  sessionId: string;
  accessToken?: string;
  refreshToken?: string;
}

/**
 * 로그인 성공 시 accessToken·sessionId·refreshToken 일괄 저장
 */
export const setAuthTokens = ({ sessionId, accessToken, refreshToken }: AuthTokensInput): void => {
  setSessionId(sessionId);
  if (accessToken) {
    setJWTToken(accessToken);
  }
  if (refreshToken) {
    setRefreshToken(refreshToken);
  }
};

/**
 * JWT 토큰 디코딩 (클라이언트 사이드)
 * 주의: 서명 검증 없이 디코딩만 수행
 */
export const decodeJWTToken = (token: string): JWTTokenPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      throw new Error('Invalid token format');
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    logger.error('JWT 토큰 디코딩 실패:', error instanceof Error ? error : undefined, {
      raw: error,
    });
    return null;
  }
};

/**
 * 현재 저장된 토큰의 페이로드 조회
 */
export const getCurrentTokenPayload = (): JWTTokenPayload | null => {
  const token = getJWTToken();
  if (!token) return null;

  return decodeJWTToken(token);
};

/**
 * 토큰 만료 여부 확인
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWTToken(token);
  if (!payload || !payload.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

/**
 * 유효한 토큰인지 확인
 */
export const isValidToken = (): boolean => {
  const token = getJWTToken();
  if (!token || !looksLikeJwt(token)) return false;

  return !isTokenExpired(token);
};

// ============================================
// 🎯 테넌트 ID 관리
// ============================================

/**
 * 테넌트 ID 저장
 */
export const setTenantId = (tenantId: string): void => {
  localStorage.setItem(TENANT_ID_KEY, tenantId);
};

/**
 * 테넌트 ID 조회
 */
export const getTenantId = (): string | null => {
  return localStorage.getItem(TENANT_ID_KEY);
};

/**
 * 테넌트 ID 제거
 */
export const removeTenantId = (): void => {
  localStorage.removeItem(TENANT_ID_KEY);
};

// ============================================
// 🎯 API 요청 헤더 생성
// ============================================

/**
 * API 요청용 헤더 생성
 */
export const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};

  const accessToken = getJWTToken();
  if (accessToken && isValidToken()) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  } else {
    const sessionId = getSessionId();
    if (sessionId) {
      headers['Authorization'] = `Bearer ${sessionId}`;
    }
  }

  const tenantId = getTenantId();
  if (tenantId) {
    headers['x-tenant-id'] = tenantId;
  }

  return headers;
};

/**
 * 로그아웃 (모든 인증 정보 제거)
 */
export const clearStoredAuth = (): void => {
  removeJWTToken();
  removeSessionId();
  removeRefreshToken();
  removeTenantId();
};

/** @deprecated clearStoredAuth 사용 권장 */
export const logout = (): void => {
  clearStoredAuth();
};

// ============================================
// 🎯 권한 확인 유틸리티
// ============================================

/**
 * 사용자 권한 확인
 */
export const hasPermission = (permission: string): boolean => {
  const payload = getCurrentTokenPayload();
  if (!payload || !payload.permissions) return false;

  return payload.permissions.includes(permission);
};

/**
 * 사용자 역할 확인
 */
export const hasRole = (role: string): boolean => {
  const payload = getCurrentTokenPayload();
  if (!payload) return false;

  return payload.role === role;
};

/**
 * JWT principal entity id — entityId or linkedEntityId from login session.
 */
export function resolveJwtEntityId(payload: JWTTokenPayload | null | undefined): string {
  if (!payload) return '';
  return payload.entityId?.trim() || payload.linkedEntityId?.trim() || '';
}

/**
 * 현재 사용자 정보 조회
 */
export const getCurrentUser = (): JWTTokenPayload | null => {
  return getCurrentTokenPayload();
};
